import React, { useState, useContext, useEffect, Suspense } from 'react';
import PropTypes, { shape } from 'prop-types';
import { QueryContext, extend } from '@thd-nucleus/data-sources';
import { Link, RatingMeter, Typography } from '@one-thd/sui-atomic-components';
import { Price } from '@thd-olt-component-react/price';
import { ProductListImage } from './ProductListImage';
import { ProductListDetails } from './ProductListDetails';
import { QuickViewLazy } from './QuickViewLazy';
import useDeviceType from '../../hooks/useDeviceType';
import { isFromBundleMini, triggerQuickViewEvent } from '../../utils/product-bundle-utils';
import { PRICE_TYPES, QUICK_VIEW_REVIEWS_MENU } from '../../constants/index';
import { isProductUnavailable } from '../../utils/product-unavailable-utils';
import { getProductPricing } from '../../utils/bundle-features-utils';

const ProductListItem = ({
  product,
  accordionProps = {},
  selectableVariants,
  bundleProduct,
  priceType,
  actionComponent,
  variant = 'bundle',
  showQuickView = true,
  swapProducts,
  selectableVariantProducts
}) => {
  const { isMobile, channel } = useDeviceType();
  const { hidePrice } = bundleProduct?.features ?? {};
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isRatingOpen, setIsRatingOpen] = useState(false);
  const hideProductPrice = hidePrice && isProductUnavailable({ product });
  const { defaultVariables } = useContext(QueryContext) || {};
  const triggerViewFullDetailsAnalytics = isFromBundleMini();

  const openQuickView = (productSelection) => {
    if (!isQuickViewOpen) {
      setSelectedProduct(productSelection);
      setIsQuickViewOpen(true);
    }
  };

  const renderProductDetails = () => {
    if (variant === 'accessory') {
      const { quickView, productListModelNumber, productListRating } = product?.features ?? {};

      return (
        <div className="sui-w-full">
          {quickView && (
            <>
              <div className="sui-flex sui-gap-x-1">
                <Link
                  variant="body-base"
                  underline="hover"
                  onClick={() => openQuickView(product)}
                  data-testid="accessory-list-brand-name"
                >
                  <b>{product?.identifiers?.brandName}</b>
                </Link>
              </div>
              <Link
                variant="body-base"
                underline="hover"
                onClick={() => openQuickView(product)}
                data-testid="accessory-list-product-label"
              >
                {product?.identifiers?.productLabel}
              </Link>
            </>
          )}
          {productListModelNumber && product?.identifiers?.modelNumber && (
            <Typography variant="body-xs" color="subtle">
              Model # {product.identifiers.modelNumber}
            </Typography>
          )}
          {productListRating && product?.reviews?.ratingsReviews && (
            <div className="sui-flex sui-pt-3">
              <RatingMeter
                data-testid="accessory-list-item-rating-reviews"
                className="hover:sui-cursor-pointer"
                value={parseFloat(product?.reviews?.ratingsReviews?.averageRating)}
                label={`(${product?.reviews?.ratingsReviews?.totalReviews})`}
                target="_blank"
                edge="start"
                RatingProps={{
                  color: 'brand'
                }}
                onClick={() => {
                  setIsRatingOpen(true);
                  openQuickView(product);
                }}
              />
            </div>
          )}
        </div>
      );
    }

    // Bundle variant uses ProductListDetails
    return (
      <ProductListDetails
        product={product}
        openQuickView={openQuickView}
        accordionProps={accordionProps}
        setIsRatingOpen={setIsRatingOpen}
        isMobile={isMobile}
        selectableVariants={selectableVariants}
        templates={bundleProduct?.features?.templates}
        selectableVariantProducts={selectableVariantProducts}
        swapProducts={swapProducts}
      />
    );
  };

  useEffect(() => {
    if (isQuickViewOpen) {
      triggerQuickViewEvent(bundleProduct, selectedProduct);
    }
  }, [isQuickViewOpen, bundleProduct, selectedProduct]);

  const pricing = getProductPricing({
    product,
    type: priceType
  });

  return (
    <>
      <div className="sui-flex sui-gap-4">
        <ProductListImage product={product} openQuickView={openQuickView} />
        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value, max-len */}
        <div className="sui-flex sui-flex-col sui-gap-2 sui-flex-1 md:sui-grid md:sui-grid-cols-[1fr_160px] md:sui-gap-4">
          {renderProductDetails()}
          {!hideProductPrice && (
            // eslint-disable-next-line tailwindcss/no-arbitrary-value
            <div className="[&_div]:!sui-text-left">
              <Price
                data-testid="product-list-item-price"
                channel={channel}
                itemId={product?.itemId}
                large={false}
                hideLimitPerOrder
                displayEachUom={false}
                clsRemediation={{
                  placeholders: true,
                  preservePlaceholders: true
                }}
                product={{
                  itemId: product.itemId,
                  identifiers: { itemId: product.itemId },
                  pricing
                }}
                stackWasPrice
                quantity={product?.quantity}
                type={priceType}
              />
            </div>
          )}
          {actionComponent && (
            <div className="sui-flex sui-flex-col sui-gap-2 sui-w-full md:sui-gap-4">
              {actionComponent}
            </div>
          )}
        </div>
      </div>

      {showQuickView && typeof window !== 'undefined' && isQuickViewOpen && (
        <Suspense fallback={<div />}>
          <QuickViewLazy
            isOpen={isQuickViewOpen}
            onClose={() => {
              setIsQuickViewOpen(false);
              setSelectedProduct(null);
              setIsRatingOpen(false);
            }}
            fetch={isQuickViewOpen}
            itemId={selectedProduct?.itemId}
            initialMenu={isRatingOpen ? QUICK_VIEW_REVIEWS_MENU : null}
            paginateCarousel={false}
            openDetailsInNewTab
            triggerViewFullDetailsAnalytics={triggerViewFullDetailsAnalytics}
            defaultVariables={defaultVariables}
          />
        </Suspense>
      )}
    </>
  );
};

ProductListItem.propTypes = {
  product: PropTypes.shape({
    itemId: PropTypes.string,
    pricing: PropTypes.shape({}),
    quantity: PropTypes.number,
    features: PropTypes.shape({
      quickView: PropTypes.bool,
      productListModelNumber: PropTypes.bool,
      productListRating: PropTypes.bool
    }),
    identifiers: PropTypes.shape({
      brandName: PropTypes.string,
      productLabel: PropTypes.string,
      modelNumber: PropTypes.string
    }),
    reviews: PropTypes.shape({
      ratingsReviews: PropTypes.shape({
        averageRating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        totalReviews: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      })
    })
  }).isRequired,
  priceType: PropTypes.oneOf(Object.values(PRICE_TYPES)),
  accordionProps: PropTypes.shape({}),
  selectableVariants: PropTypes.shape({}),
  bundleProduct: PropTypes.shape({
    features: PropTypes.shape({
      templates: PropTypes.shape({})
    })
  }),
  actionComponent: PropTypes.node,
  variant: PropTypes.oneOf(['bundle', 'accessory']),
  showQuickView: PropTypes.bool,
  swapProducts: PropTypes.func,
  selectableVariantProducts: PropTypes.arrayOf(shape({})).isRequired
};

ProductListItem.defaultProps = {
  accordionProps: {},
  selectableVariants: undefined,
  bundleProduct: undefined,
  actionComponent: null,
  variant: 'bundle',
  priceType: PRICE_TYPES.SIMPLE,
  showQuickView: true,
  swapProducts: () => {}
};

ProductListItem.displayName = 'ProductListItem';

ProductListItem.dataModel = extend(Price);

export { ProductListItem };
